import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import Avatar from './ui/Avatar';
import { visibleModules, moduleNav } from '../config/modules';
import styles from './MobileMenu.module.css';

/**
 * Everything, on a phone.
 *
 * The desktop sidebar is hidden below 1024px and the tab bar only carries five
 * destinations, so on a phone most of the app had no route to it at all. This
 * is that route — and deliberately a full-screen list rather than a drawer
 * sliding in from the edge: a drawer on a 390px screen is a narrow column with
 * the page showing through beside it, which is harder to read and harder to hit
 * than simply using the whole screen for the one thing you are doing.
 *
 * Same groups and same links as the sidebar, from the same module registry, so
 * it stays role-aware without being maintained twice.
 */
function MobileMenu({ open, onClose }) {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();

  // The page behind must not scroll while this is over it.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const groups = visibleModules(user)
    .map((m) => ({ id: m.id, name: m.name, items: moduleNav(m, user) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className={styles.sheet} role="dialog" aria-modal="true" aria-label="Menu">
      <header className={styles.head}>
        <div className={styles.who}>
          <Avatar user={user} size="md" />
          <div className={styles.whoText}>
            <span className={styles.whoName}>{user?.firstName} {user?.lastName}</span>
            <span className={styles.whoRole}>{user?.role}</span>
          </div>
        </div>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close menu">
          <X size={22} />
        </button>
      </header>

      <nav className={styles.body}>
        {groups.map((group) => (
          <section key={group.id} className={styles.group}>
            <h2 className={styles.groupLabel}>{group.name}</h2>
            <div className={styles.items}>
              {group.items.map((item) => {
                const Icon = item.icon;
                const badge = item.id === 'messages' ? unreadCount : 0;
                return (
                  <Link key={item.id} to={item.path} className={styles.item} onClick={onClose}>
                    {Icon && <Icon size={19} strokeWidth={1.75} className={styles.itemIcon} />}
                    <span className={styles.itemLabel}>{item.label}</span>
                    {badge > 0 && <span className={styles.itemBadge}>{badge > 9 ? '9+' : badge}</span>}
                  </Link>
                );
              })}
            </div>
          </section>
        ))}

        <button type="button" className={styles.signOut} onClick={() => { onClose(); logout(); }}>
          <LogOut size={18} /> Sign out
        </button>
      </nav>
    </div>
  );
}

export default MobileMenu;
